import * as Sentry from '@sentry/react';
import * as React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const DEFAULT_SENTRY_DSN =
  'https://e8b6877fbbe91e168d3d8884ca88c95d@o4511208258797568.ingest.de.sentry.io/4511208262664272';
const TELEGRAM_POST_EVENT_METHOD_NOT_FOUND_ERROR =
  'Error invoking postEvent: Method not found';

const containsTelegramPostEventMethodNotFoundErrorText = (
  value: unknown,
): boolean => {
  if (typeof value === 'string') {
    return value.includes(TELEGRAM_POST_EVENT_METHOD_NOT_FOUND_ERROR);
  }

  return false;
};

const containsTelegramPostEventMethodNotFoundException = (
  value: unknown,
): boolean => {
  if (value instanceof Error) {
    return (
      containsTelegramPostEventMethodNotFoundErrorText(value.message) ||
      containsTelegramPostEventMethodNotFoundErrorText(value.stack)
    );
  }

  if (!value || typeof value !== 'object') {
    return containsTelegramPostEventMethodNotFoundErrorText(value);
  }

  const errorLike = value as {
    message?: unknown;
    stack?: unknown;
  };

  return (
    containsTelegramPostEventMethodNotFoundErrorText(errorLike.message) ||
    containsTelegramPostEventMethodNotFoundErrorText(errorLike.stack)
  );
};

const eventContainsTelegramPostEventMethodNotFoundException = (
  event: Sentry.Event,
): boolean =>
  containsTelegramPostEventMethodNotFoundErrorText(event.message) ||
  event.exception?.values?.some(exception =>
    containsTelegramPostEventMethodNotFoundErrorText(exception.value),
  ) === true;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? DEFAULT_SENTRY_DSN,
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
  ignoreErrors: [TELEGRAM_POST_EVENT_METHOD_NOT_FOUND_ERROR],
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      stripBasename: true,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 1,
  tracePropagationTargets: ['localhost', import.meta.env.VITE_API_URL],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  beforeSend(event, hint) {
    if (
      eventContainsTelegramPostEventMethodNotFoundException(event) ||
      containsTelegramPostEventMethodNotFoundException(hint?.originalException)
    ) {
      return null;
    }

    return event;
  },
});
